import { useEffect, useRef, useState } from 'react';
import { styled } from 'styled-components';
import { useNewsletterSignup } from '@/hooks/useNewsletterSignup';

interface JoinInCaptureProps {
  /** Body text shown before the "join in." link. */
  sentence: string;
  /** Where this capture point lives, e.g. "latest-news". */
  source: string;
  /** The project page has no route params - identity comes from state, so the
      parent passes the project name (group.group) explicitly. */
  project?: string;
  align?: 'left' | 'center';
}

/* Typography (family/size/weight/line-height/color) is deliberately NOT set
   here - the capture must be indistinguishable from the body copy around it
   (founder, 2026-09-02), so everything inherits from the placement's
   container. Only the "join in." link carries its own treatment. */
/* font-family: inherit is load-bearing on every element here: globalStyles
   applies Oswald via the universal selector directly to each element, so
   passive inheritance never happens - each element must opt back in to the
   container's family explicitly. */
const Wrapper = styled.div<{ $align: 'left' | 'center' }>`
  font-family: inherit;
  text-align: ${({ $align }) => $align};
`;

const Sentence = styled.span`
  font-family: inherit;
`;

const JoinLink = styled.button`
  background: none;
  border: none;
  border-bottom: 2px solid #ec008c;
  color: #ec008c;
  cursor: pointer;
  font-family: inherit;
  font-size: inherit;
  font-weight: inherit;
  line-height: inherit;
  padding: 0;
`;

const EmailInput = styled.input<{ $invalid: boolean }>`
  background: transparent;
  border: none;
  border-bottom: 2px solid ${({ $invalid }) => ($invalid ? '#ec008c' : '#999')};
  border-radius: 0;
  color: inherit;
  font-family: inherit;
  font-weight: inherit;
  margin-right: 8px;
  max-width: 220px;
  outline: none;
  padding: 0 2px;
  /* 16px minimum so iOS Safari doesn't zoom the page when the field focuses. */
  font-size: 16px;

  &::placeholder {
    color: #999;
  }
`;

const ErrorText = styled.div`
  color: #ec008c;
  font-family: inherit;
  font-size: 13px;
  margin-top: 4px;
`;

const PrivacyNote = styled.div`
  color: #999;
  font-family: inherit;
  font-size: 11px;
  margin-top: 4px;
`;

export const JoinInCapture = ({
  sentence,
  source,
  project,
  align = 'left',
}: JoinInCaptureProps) => {
  const [open, setOpen] = useState(false);
  const { email, changeEmail, error, done, submit } = useNewsletterSignup(source, project);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  if (done) {
    return (
      <Wrapper $align={align}>
        <Sentence>You are in. The next story from the farm is on its way.</Sentence>
      </Wrapper>
    );
  }

  if (!open) {
    return (
      <Wrapper $align={align}>
        <Sentence>{sentence} </Sentence>
        <JoinLink type="button" onClick={() => setOpen(true)}>
          join in.
        </JoinLink>
      </Wrapper>
    );
  }

  return (
    <Wrapper $align={align}>
      {/* noValidate: the browser's native email bubble would replace our
          designed inline error, so validation stays in handleSubmit. */}
      <form noValidate onSubmit={submit} style={{ display: 'inline', fontFamily: 'inherit' }}>
        <EmailInput
          ref={inputRef}
          type="email"
          placeholder="your@email.com"
          value={email}
          $invalid={error !== null}
          onChange={(e) => changeEmail(e.target.value)}
          onKeyDown={(e) => {
            // Explicit Enter handling on top of the form's implicit
            // submission, so the key works even where that is interfered with.
            if (e.key === 'Enter') submit(e);
          }}
        />
        <JoinLink type="submit">join in.</JoinLink>
      </form>
      {error === 'invalid' && (
        <ErrorText>That email doesn&apos;t look right — check the address.</ErrorText>
      )}
      {error === 'failed' && <ErrorText>Something went wrong — please try again.</ErrorText>}
      <PrivacyNote>Unsubscribe anytime.</PrivacyNote>
    </Wrapper>
  );
};
